import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { ChevronDown, Copy, Download, Loader, MapPin, Plus, Printer, RotateCcw, Search, Users, X } from "lucide-react";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import { API_BASE_URL } from "../../../../../api";
import MissionInlineForm, { calculateDurationDays } from "./MissionInlineForm";
import "../../parametres/parametresStyles/ParamConges.css";
import "../../Offboarding/OffboardingStyles/Offboarding.css";
import "../MesrequetesStyles/DemandeConges.css";

const CREATED_AS_ADMIN = "ADMIN";

/**
 * ── SearchableCombobox ──
 * Champ hybride : liste déroulante (options prédéfinies) + saisie libre pour
 * rechercher/filtrer. L'utilisateur peut cliquer pour ouvrir la liste,
 * taper pour filtrer les suggestions, sélectionner une suggestion,
 * ou simplement continuer à taper un texte libre.
 */
const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);

    useEffect(() => {
        setQuery(value || "");
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleSelect = (opt) => {
        setQuery(opt.label);
        onChange(opt.value);
        setOpen(false);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        onChange(val);
        setOpen(true);
    };

    const handleClear = () => {
        setQuery("");
        onChange("");
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="pcInput"
                    style={{ width: "100%", fontSize: 13, paddingRight: 56 }}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (
                        <button
                            type="button"
                            onClick={handleClear}
                            style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }}
                            title="Effacer"
                        >
                            <X size={13} />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }}
                        title="Afficher la liste"
                    >
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>

            {open && filteredOptions.length > 0 && (
                <div
                    style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto",
                    }}
                >
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div
                                key={opt.value}
                                onClick={() => handleSelect(opt)}
                                style={{
                                    padding: "8px 12px", fontSize: 13, cursor: "pointer",
                                    background: isSelected ? "#eff6ff" : "#fff",
                                    color: isSelected ? "#204385" : "#374151",
                                    fontWeight: isSelected ? 600 : 400,
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}
                            >
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}

            {open && query && filteredOptions.length === 0 && (
                <div
                    style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px",
                        fontSize: 12, color: "#9ca3af",
                    }}
                >
                    Aucun résultat — la recherche libre reste appliquée
                </div>
            )}
        </div>
    );
};

const DemandeMissionAdmin = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createdRequests, setCreatedRequests] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [searchCollaborateur, setSearchCollaborateur] = useState("");
    const [selectedCollaborateurs, setSelectedCollaborateurs] = useState([]);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);

    // ── Filtres de l'onglet "Ordres de mission créés" ──
    const [filterCollab, setFilterCollab] = useState("");
    const [filterStatut, setFilterStatut] = useState("");
    const [filterLieu, setFilterLieu] = useState("");
    const [filterDateOp, setFilterDateOp] = useState("");

    const handleResetCreatedFilters = () => {
        setFilterCollab("");
        setFilterStatut("");
        setFilterLieu("");
        setFilterDateOp("");
    };

    const headers = useMemo(() => ({
        uid: sessionStorage.getItem("uid"),
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        "X-Tenant-Id": sessionStorage.getItem("selectedCompanyId"),
        "Content-Type": "application/json",
        Accept: "application/json",
    }), []);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/api/users`, { headers })
            .then((resp) => setUsers(resp.data || []))
            .catch(() => setUsers([]))
            .finally(() => setLoading(false));
    }, [headers]);

    const fetchCreatedRequests = () => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/api/mission-requests/requests/created-by-me?createdAsRole=${CREATED_AS_ADMIN}`, { headers })
            .then((resp) => setCreatedRequests(resp.data || []))
            .catch(() => setCreatedRequests([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchCreatedRequests();
    }, [headers]);

    const collaborateursFiltres = useMemo(() => {
        const isActif = (u) => u && u.enabled !== false && u.actif !== false && u.isActif !== false && u.statut !== "INACTIF";
        const activeUsers = (users || []).filter(isActif);
        if (!searchCollaborateur.trim()) return activeUsers;
        const term = searchCollaborateur.toLowerCase();
        return activeUsers.filter((c) =>
            c.firstName?.toLowerCase().includes(term) ||
            c.lastName?.toLowerCase().includes(term) ||
            c.employeeCode?.toLowerCase().includes(term) ||
            c.email?.toLowerCase().includes(term)
        );
    }, [users, searchCollaborateur]);

    const toggleCollaborateur = (collabId) => {
        setSelectedCollaborateurs((prev) =>
            prev.includes(collabId) ? prev.filter((id) => id !== collabId) : [...prev, collabId]
        );
    };

    const handleSubmit = async (payload) => {
        if (selectedCollaborateurs.length === 0) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: "Veuillez selectionner au moins un collaborateur",
                confirmButtonColor: "#204385",
            });
            return;
        }

        setLoading(true);
        try {
            const { justificatifs, ...requestData } = payload;
            await axios.post(`${API_BASE_URL}/api/mission-requests/admin/create-for-collab`, {
                ...requestData,
                collaborateurIds: selectedCollaborateurs,
            }, { headers });

            await Swal.fire({
                icon: "success",
                title: "Succès",
                text: `${selectedCollaborateurs.length} demande(s) créée(s)`,
                timer: 1800,
                showConfirmButton: false,
            });

            setSelectedCollaborateurs([]);
            setSearchCollaborateur("");
            fetchCreatedRequests();
            setShowCreateModal(false);
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Erreur creation demandes";
            Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonColor: "#d33" });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async (demande, print = false) => {
        try {
            const doc = new jsPDF();
            // Reserve a blank letterhead area, as on the work certificate.

            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.setTextColor(42, 92, 169);
            doc.text("ORDRE DE MISSION", 20, 68);
            doc.setFont("helvetica", "normal");
            doc.text(`Ref. ${demande.ordreMissionCode || "N/A"}`, 20, 76);

            let y = 91;
            const addTitle = (title) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.setTextColor(42, 92, 169);
                doc.text(title, 20, y);
                y += 7;
            };
            const addField = (label, value) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                const labelText = `${label} : `;
                doc.text(labelText, 20, y);
                doc.setFont("helvetica", "normal");
                doc.text(String(value || "-"), 20 + doc.getTextWidth(labelText), y);
                y += 6;
            };
            const addParagraph = (text) => {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                const lines = doc.splitTextToSize(text || "-", 170);
                doc.text(lines, 20, y);
                y += lines.length * 5 + 3;
            };

            addTitle("1. Identification du collaborateur");
            addField("Nom et prénom", demande.employeeName || "Collaborateur");
            addField("Fonction", demande.employeeJobName);
            addField("CIN / Passeport", demande.employeeCin || demande.employeePasseport);
            y += 4;

            addTitle("2. Objet de la mission");
            addParagraph(`${demande.ordreMissionLibelle || ""}${demande.ordreMissionObjet ? ` - ${demande.ordreMissionObjet}` : ""}`);
            y += 4;

            addTitle("3. Lieu et durée de la mission");
            const lieu = [demande.ordreMissionPays, demande.ordreMissionVille, demande.ordreMissionAdresse]
                .filter(Boolean)
                .join(", ") || demande.ordreMissionLieu;
            addField("Lieu de la mission", lieu);
            addField("Date de départ", formatDate(demande.ordreMissionDateDebut));
            addField("Date de retour", formatDate(demande.ordreMissionDateFin));
            const duration = calculateDurationDays(demande.ordreMissionDateDebut, demande.ordreMissionDateFin);
            addField("Durée totale", duration ? `${duration} jour${duration > 1 ? "s" : ""}` : "-");
            y += 4;

            addTitle("4. Prise en charge et conditions");
            addParagraph("Les frais liés à la mission sont pris en charge par la société, conformément à la politique interne et sur présentation des justificatifs.");
            y += 4;

            addTitle("5. Engagement collaborateur");
            addParagraph(demande.ordreMissionEngagementsCollaborateur);
            doc.setFontSize(11);
            doc.text(`Fait à Agadir, le ${new Date(demande.createdAt || Date.now()).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}`, 105, y + 8);
            if (print) {
                const printFrame = document.createElement("iframe");
                const blobUrl = URL.createObjectURL(doc.output("blob"));
                printFrame.style.display = "none";
                printFrame.src = blobUrl;
                printFrame.onload = () => {
                    printFrame.contentWindow?.print();
                    setTimeout(() => { printFrame.remove(); URL.revokeObjectURL(blobUrl); }, 1000);
                };
                document.body.appendChild(printFrame);
                return;
            }
            doc.save(`Ordre_Mission_${demande.ordreMissionCode || demande.id}.pdf`);
        } catch (error) {
            console.error("PDF generation error", error);
            Swal.fire({ icon: "error", title: "Erreur", text: "Une erreur est survenue lors de la génération du PDF.", confirmButtonColor: "#ef4444" });
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const renderLocation = (demande) => {
        const parts = [demande.ordreMissionPays, demande.ordreMissionVille, demande.ordreMissionAdresse].filter(Boolean);
        return parts.length > 0 ? parts.join(", ") : demande.ordreMissionLieu;
    };

    const handleDuplicate = (demande) => {
        setSelectedDemande(demande);
        setShowDuplicateModal(true);
    };

    const handleDuplicateSubmit = async (payload) => {
        if (!selectedDemande) return;
        setLoading(true);
        try {
            const { justificatifs, ...requestData } = payload;
            await axios.post(`${API_BASE_URL}/api/mission-requests/admin/create-for-collab`, {
                ...requestData,
                collaborateurIds: [selectedDemande.employeeId],
            }, { headers });

            await Swal.fire({
                icon: "success",
                title: "Succès",
                text: "Ordre de mission dupliqué",
                timer: 1600,
                showConfirmButton: false,
            });

            setShowDuplicateModal(false);
            setSelectedDemande(null);
            fetchCreatedRequests();
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Erreur duplication";
            Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonColor: "#d33" });
        } finally {
            setLoading(false);
        }
    };

    const buildInitialMission = (demande) => ({
        natureMissionId: demande.natureMissionId || "",
        code: demande.ordreMissionCode || "",
        libelle: demande.ordreMissionLibelle || "",
        description: demande.ordreMissionDescription || "",
        objetMission: demande.ordreMissionObjet || "",
        dateDebut: demande.ordreMissionDateDebut || "",
        dateFin: demande.ordreMissionDateFin || "",
        zoneMission: demande.ordreMissionZoneMission || "EN_VILLE",
        pays: demande.ordreMissionPays || "",
        ville: demande.ordreMissionVille || "",
        adresse: demande.ordreMissionAdresse || "",
        engagementsCollaborateur: demande.ordreMissionEngagementsCollaborateur || "",
        workflow: demande.workflowLevel || "both",
    });

    // ── Options des filtres (liées entre elles : un filtre actif restreint
    // les suggestions proposées par les autres) ──
    const matchesCreatedFilters = (demande, { skipCollab, skipStatut, skipLieu, skipDate } = {}) => {
        if (!skipCollab && filterCollab) {
            const val = filterCollab.toLowerCase();
            const collab = `${demande.employeeName || ""} ${demande.employeeMatricule || ""}`.toLowerCase();
            if (!collab.includes(val)) return false;
        }
        if (!skipStatut && filterStatut) {
            if ((demande.status || "").toLowerCase() !== filterStatut.toLowerCase()) return false;
        }
        if (!skipLieu && filterLieu) {
            const val = filterLieu.toLowerCase();
            const lieu = (renderLocation(demande) || "").toLowerCase();
            if (!lieu.includes(val)) return false;
        }
        if (!skipDate && filterDateOp) {
            const debut = demande.ordreMissionDateDebut ? new Date(demande.ordreMissionDateDebut) : null;
            const fin = demande.ordreMissionDateFin ? new Date(demande.ordreMissionDateFin) : debut;
            const target = new Date(filterDateOp);
            if (!debut || target < new Date(debut.toDateString()) || target > new Date((fin || debut).toDateString())) {
                return false;
            }
        }
        return true;
    };

    const collabOptions = useMemo(() => {
        const map = new Map();
        (createdRequests || [])
            .filter((d) => matchesCreatedFilters(d, { skipCollab: true }))
            .forEach((d) => {
                if (d.employeeName && !map.has(d.employeeName)) {
                    map.set(d.employeeName, {
                        value: d.employeeName,
                        label: d.employeeMatricule ? `${d.employeeName} (${d.employeeMatricule})` : d.employeeName,
                    });
                }
            });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [createdRequests, filterStatut, filterLieu, filterDateOp]);

    const statutOptions = useMemo(() => {
        const set = new Set();
        (createdRequests || [])
            .filter((d) => matchesCreatedFilters(d, { skipStatut: true }))
            .forEach((d) => { if (d.status) set.add(d.status); });
        return Array.from(set).sort().map((s) => ({ value: s, label: s }));
    }, [createdRequests, filterCollab, filterLieu, filterDateOp]);

    const lieuOptions = useMemo(() => {
        const set = new Set();
        (createdRequests || [])
            .filter((d) => matchesCreatedFilters(d, { skipLieu: true }))
            .forEach((d) => {
                const lieu = renderLocation(d);
                if (lieu) set.add(lieu);
            });
        return Array.from(set).sort().map((l) => ({ value: l, label: l }));
    }, [createdRequests, filterCollab, filterStatut, filterDateOp]);

    const filteredCreatedRequests = useMemo(() => {
        return (createdRequests || []).filter((d) => matchesCreatedFilters(d));
    }, [createdRequests, filterCollab, filterStatut, filterLieu, filterDateOp]);

    const renderCreatedRequests = () => (
        <div className="obCard obCard--noPad">
            <div className="obCard__body">
                <div className="obControls">
                    <div>
                        <h2 className="obCard__title">Ordres de mission créés</h2>
                        <div className="obMuted">
                            Demandes créées par vous pour les collaborateurs.
                        </div>
                    </div>
                </div>

                {/* ── Filtres ── */}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "flex-end",
                        gap: 16,
                        marginBottom: 18,
                        padding: 16,
                        background: "#f8fafc",
                        borderRadius: 10,
                        border: "1px solid #e2e8f0",
                    }}
                >
                    <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                            Collaborateur / Matricule
                        </label>
                        <SearchableCombobox
                            value={filterCollab}
                            onChange={setFilterCollab}
                            options={collabOptions}
                            placeholder="Nom ou matricule..."
                        />
                    </div>
                    <div style={{ minWidth: 180, flex: "1 1 180px" }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                            Lieu
                        </label>
                        <SearchableCombobox
                            value={filterLieu}
                            onChange={setFilterLieu}
                            options={lieuOptions}
                            placeholder="Pays, ville..."
                        />
                    </div>
                    <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                            Statut
                        </label>
                        <SearchableCombobox
                            value={filterStatut}
                            onChange={setFilterStatut}
                            options={statutOptions}
                            placeholder="Rechercher un statut..."
                        />
                    </div>
                    <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                            Date (période)
                        </label>
                        <input
                            type="date"
                            className="pcInput"
                            style={{ width: "100%", fontSize: 13 }}
                            value={filterDateOp}
                            onChange={(e) => setFilterDateOp(e.target.value)}
                        />
                    </div>
                    <div style={{ flexShrink: 0 }}>
                        <button
                            onClick={handleResetCreatedFilters}
                            className="pcBtn pcBtn--ghost"
                            style={{ padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 13, height: 38, whiteSpace: "nowrap" }}
                            type="button"
                        >
                            <RotateCcw size={14} /> Réinitialiser
                        </button>
                    </div>
                </div>

                <div className="obTableWrap">
                    <table className="obTable">
                        <thead>
                        <tr>
                            <th>Code</th>
                            <th>Collaborateur</th>
                            <th>Mission</th>
                            <th>Période</th>
                            <th>Lieu</th>
                            <th>Statut</th>
                            <th className="is-right">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading && createdRequests.length === 0 ? (
                            <tr><td colSpan={7} className="pcEmptyCell">Chargement...</td></tr>
                        ) : filteredCreatedRequests.length === 0 ? (
                            <tr><td colSpan={7} className="pcEmptyCell">Aucune demande trouvée</td></tr>
                        ) : filteredCreatedRequests.map((demande) => (
                            <tr key={demande.id}>
                                <td><span className="pcCode" style={{ background: "#eef2ff", border: "1px solid #c7d2fe", color: "#204385" }}>{demande.ordreMissionCode}</span></td>
                                <td>
                                    <div className="pcStrong">{demande.employeeName || "-"}</div>
                                    <div className="pcMuted" style={{ fontSize: 12 }}>{demande.employeeMatricule || ""}</div>
                                </td>
                                <td>
                                    <div className="pcStrong">{demande.ordreMissionLibelle}</div>
                                    <div className="pcMuted" style={{ fontSize: 12 }}>{demande.natureMissionLibelle || ""}</div>
                                </td>
                                <td>{formatDate(demande.ordreMissionDateDebut)} - {formatDate(demande.ordreMissionDateFin)}</td>
                                <td>
                                    {renderLocation(demande) ? (
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                            <MapPin size={13} /> {renderLocation(demande)}
                                        </span>
                                    ) : "-"}
                                </td>
                                <td><span className="pcPill is-on">{demande.status}</span></td>
                                <td className="is-right">
                                    {demande.status === "APPROVED" && (
                                        <>
                                            <button className="pcIconBtn" type="button" title="Télécharger l'ordre de mission PDF" onClick={() => handleDownloadPdf(demande)} disabled={loading}>
                                                <Download size={16} />
                                            </button>
                                            <button className="pcIconBtn" type="button" title="Imprimer l'ordre de mission" onClick={() => handleDownloadPdf(demande, true)} disabled={loading}>
                                                <Printer size={16} />
                                            </button>
                                        </>
                                    )}
                                    <button className="pcIconBtn" type="button" title="Dupliquer" onClick={() => handleDuplicate(demande)} disabled={loading}>
                                        <Copy size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    return (
        <div className="obPage">
            <div className="obHeader" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                    <h1>Saisie des ordres de mission</h1>
                    <p>Saisissez un ordre de mission pour n'importe quel collaborateur. Les demandes seront approuvées automatiquement.</p>
                </div>
                <button className="obBtn obBtn--primary" type="button" onClick={() => setShowCreateModal(true)}>
                    <Plus size={18} /> Saisir un ordre de mission
                </button>
            </div>

            <div className="obWrap">
                {renderCreatedRequests()}
                {showCreateModal && (
                    <div className="pcModal">
                        <div className="pcModal__panel pcModal__panel--wide">
                            <div className="pcModal__header">
                                <div className="pcModal__headerLeft">
                                    <div className="pcModal__title">Saisir un ordre de mission</div>
                                    <div className="pcModal__subtitle">Renseignez l'ordre de mission et sélectionnez les collaborateurs concernés.</div>
                                </div>
                                <button className="pcIconBtn pcIconBtn--close" type="button" onClick={() => setShowCreateModal(false)} aria-label="Fermer"><X size={15} /></button>
                            </div>
                            <div className="pcModal__body">
                            <MissionInlineForm
                                formId="adminMissionForm"
                                onSubmit={handleSubmit}
                                loading={loading}
                                submitLabel={`Envoyer ${selectedCollaborateurs.length || "0"} ordre${selectedCollaborateurs.length > 1 ? "s" : ""}`}
                                showWorkflow={false}
                            >
                                <div className="pcWizStep">
                                    <div className="pcWizStep__intro">
                                        <div className="pcWizStep__introIcon"><Users size={20} /></div>
                                        <div>
                                            <div className="pcWizStep__introTitle">Collaborateurs concernés</div>
                                            <div className="pcWizStep__introSub">{selectedCollaborateurs.length} collaborateur(s) selectionne(s)</div>
                                        </div>
                                    </div>

                                    <div className="pcWizField is-full">
                                        <label>Rechercher un collaborateur</label>
                                        <div style={{ position: "relative" }}>
                                            <Search size={18} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
                                            <input
                                                className="pcInput"
                                                type="text"
                                                placeholder="Nom, matricule, email..."
                                                value={searchCollaborateur}
                                                onChange={(e) => setSearchCollaborateur(e.target.value)}
                                                style={{ paddingLeft: 40 }}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8, padding: 8 }}>
                                        {loading && users.length === 0 ? (
                                            <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
                                                <Loader size={24} className="spin" />
                                            </div>
                                        ) : collaborateursFiltres.length === 0 ? (
                                            <div className="pcEmptyCell">Aucun collaborateur trouvé</div>
                                        ) : collaborateursFiltres.map((collab) => (
                                            <label key={collab.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 10, borderRadius: 6, cursor: "pointer", border: "1px solid #e5e7eb", marginBottom: 6 }}>
                                                <input type="checkbox" checked={selectedCollaborateurs.includes(collab.id)} onChange={() => toggleCollaborateur(collab.id)} />
                                                <div>
                                                    <div className="pcStrong">{collab.firstName} {collab.lastName}</div>
                                                    <div className="pcMuted" style={{ fontSize: 12 }}>{collab.employeeCode} - {collab.email}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </MissionInlineForm>
                        </div>
                        </div>
                    </div>
                )}
            </div>
            {showDuplicateModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Dupliquer l'ordre de mission</div>
                                <div className="pcModal__subtitle">Duplication de l'ordre de mission pour {selectedDemande.employeeName}. Modifiez les informations avant d'envoyer la demande.</div>
                            </div>
                            <button
                                className="pcIconBtn pcIconBtn--close"
                                onClick={() => { setShowDuplicateModal(false); setSelectedDemande(null); }}
                                aria-label="Fermer"
                            >
                                <X size={15} />
                            </button>
                        </div>
                        <div className="pcModal__body">
                            <MissionInlineForm
                                formId="adminDuplicateMissionForm"
                                onSubmit={handleDuplicateSubmit}
                                loading={loading}
                                submitLabel="Dupliquer et envoyer l'ordre de mission"
                                initialMissionData={buildInitialMission(selectedDemande)}
                                initialUrgent={selectedDemande.urgent}
                                initialMotif={selectedDemande.motif}
                                showNotes={false}
                                showJustificatifs={false}
                                showComment={false}
                                showWorkflow={false}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DemandeMissionAdmin;
